// InstallButton — T-066. Real PWA installation trigger.
// Captures eforeinstallprompt to prompt installation on Chromium/Android.
// On iOS Safari, presents step-by-step Add to Home Screen instructions.
// Hides completely when running in standalone mode or on unsupported platforms.

import { useEffect, useState, useCallback, useRef, type JSX } from "react";
import { useI18n } from "../../i18n";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallButtonProps {
  variant?: "header" | "footer" | "banner";
  className?: string;
}

export function InstallButton({ variant = "header", className }: InstallButtonProps): JSX.Element | null {
  const { t } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  const installBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Check if running in standalone display mode
    const standalone =
      (typeof window !== "undefined" &&
        ((typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
          (navigator as unknown as { standalone?: boolean }).standalone === true)) ||
      false;
    setIsStandalone(standalone);

    // Detect iOS
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isAppleMobile = /iphone|ipad|ipod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    setIsIOS(isAppleMobile);

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Handle Escape key and focus management for iOS modal
  useEffect(() => {
    if (!showIOSModal) return;

    closeBtnRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showIOSModal]);

  const handleCloseModal = () => {
    setShowIOSModal(false);
    installBtnRef.current?.focus();
  };

  const handleInstallClick = useCallback(async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setDeferredPrompt(null);
      }
    } else if (isIOS) {
      setShowIOSModal(true);
    }
  }, [deferredPrompt, isIOS]);

  // Already installed / standalone
  if (isStandalone) {
    return null;
  }

  // Not installable (no beforeinstallprompt and not iOS)
  if (!deferredPrompt && !isIOS) {
    return null;
  }

  const baseButton = (
    <button
      ref={installBtnRef}
      type="button"
      onClick={handleInstallClick}
      data-testid="pwa-install-button"
      className={
        className ??
        (variant === "header"
          ? "inline-flex items-center gap-1.5 border border-[var(--color-lime)]/50 bg-[var(--color-slab)] px-3 py-1.5 font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-lime)] hover:bg-[var(--color-lime)] hover:text-[var(--color-void)] active:scale-95 transition-all"
          : "text-left text-[var(--color-ink)] hover:text-[var(--color-ink-hi)] transition-colors")
      }
      style={{ borderRadius: "var(--radius-sharp)" }}
    >
      <span aria-hidden="true" className="text-sm">⤓</span>
      <span>{t("pwa.installButton")}</span>
    </button>
  );

  return (
    <>
      {baseButton}

      {showIOSModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-ios-modal-title"
          data-testid="pwa-ios-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,11,10,0.92)" }}
        >
          <div
            className="flex w-full max-w-[480px] flex-col border border-[var(--color-lime)]/40 bg-[var(--color-slab)] p-6 shadow-2xl"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            <div className="flex items-center justify-between border-b border-[var(--color-line)] pb-3">
              <h2
                id="pwa-ios-modal-title"
                className="font-display text-xl uppercase tracking-[0.02em] text-[var(--color-ink-hi)]"
              >
                {t("pwa.iosTitle")}
              </h2>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={handleCloseModal}
                className="font-subhead text-sm uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-ink-hi)]"
              >
                ✕
              </button>
            </div>

            <ol className="mt-5 flex flex-col gap-4 font-sans text-sm text-[var(--color-ink)]">
              <li className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-lime)] font-subhead text-xs font-bold text-[var(--color-void)]">
                  1
                </span>
                <span className="leading-relaxed">
                  {t("pwa.iosStep1")}{" "}
                  <span className="inline-block rounded border border-[var(--color-line-strong)] bg-[var(--color-void)] px-1.5 py-0.5 font-mono text-xs text-[var(--color-lime)]">
                    ⎋
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-lime)] font-subhead text-xs font-bold text-[var(--color-void)]">
                  2
                </span>
                <span className="leading-relaxed">
                  {t("pwa.iosStep2")}{" "}
                  <span className="inline-block rounded border border-[var(--color-line-strong)] bg-[var(--color-void)] px-1.5 py-0.5 font-mono text-xs text-[var(--color-lime)]">
                    +
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-lime)] font-subhead text-xs font-bold text-[var(--color-void)]">
                  3
                </span>
                <span className="leading-relaxed">{t("pwa.iosStep3")}</span>
              </li>
            </ol>

            <button
              type="button"
              onClick={handleCloseModal}
              className="mt-6 w-full border border-[var(--color-line-strong)] bg-[var(--color-void)] py-3 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink-hi)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
              style={{ borderRadius: "var(--radius-sharp)" }}
            >
              {t("pwa.close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
